import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Public as PublicIcon,
  Psychology as AIIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ACEATAPEnhancementContractDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            ACE & ATAP Enhancement Contract (Joint Venture)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            $136 Million - CBP Office of Information and Technology
          </Typography>
        </Box>
      </Box>

      {/* Contract Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Dev Technology Group, in joint venture with C. Mack Solutions, was awarded a $136 million three-year task 
            order to support CBP's mission to modernize and enhance the Automated Commercial Environment (ACE) and 
            Advanced Trade Analytics Program (ATAP) suites with emerging technologies and advanced analytics. This 
            strategic contract is managed through the CBP Office of Information and Technology (CIO) and leverages 
            Dev Technology's SBA-approved mentor status to deliver cutting-edge capabilities for U.S. trade processing 
            and analytics.
          </Typography>
        </CardContent>
      </Card>

      {/* Contract Details */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Details
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ACE & ATAP Enhancement Contract Details">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '25%', bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Value
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>$136,000,000</strong> (One Hundred Thirty-Six Million Dollars)
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Joint Venture Partners
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Dev Technology Group</strong> (Prime) + <strong>C. Mack Solutions</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    SBA-approved mentor-protégé joint venture
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Duration
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>3 Years</strong> (2025-2028)
                  </Typography>
                  <Typography variant="caption" display="block">
                    Award Date: 2025
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contracting Office
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>CBP Office of Information and Technology (CIO)</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    U.S. Customs and Border Protection
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Contract Focus
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Modernization, Emerging Technologies, Advanced Analytics</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    ACE and ATAP platform enhancement and expansion
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                >
                  Status
                </TableCell>
                <TableCell>
                  <Chip label="Active" color="success" />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Current operations through 2028
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Systems Supported */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Systems Supported
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #003366' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  ACE (Automated Commercial Environment)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                CBP's primary trade processing system for imports and exports.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Processing:</strong> Entry filing, cargo release, duty collection</Typography></li>
                <li><Typography variant="body2"><strong>Modernization:</strong> Legacy system upgrades and cloud migration</Typography></li>
                <li><Typography variant="body2"><strong>User Interface:</strong> Enhanced portals for trade community</Typography></li>
                <li><Typography variant="body2"><strong>Integration:</strong> Partner Government Agency (PGA) connections</Typography></li>
                <li><Typography variant="body2"><strong>Performance:</strong> System optimization and scalability improvements</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  ATAP (Advanced Trade Analytics Program)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced analytics platform for trade intelligence and enforcement.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Advanced Analytics:</strong> Machine learning, predictive modeling</Typography></li>
                <li><Typography variant="body2"><strong>Trade Intelligence:</strong> Pattern recognition, anomaly detection</Typography></li>
                <li><Typography variant="body2"><strong>Risk Assessment:</strong> Automated targeting and screening</Typography></li>
                <li><Typography variant="body2"><strong>Data Visualization:</strong> Interactive dashboards and reporting</Typography></li>
                <li><Typography variant="body2"><strong>Big Data:</strong> Processing billions of trade transactions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Scope of Work */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Scope of Work & Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Emerging Technologies
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Integration of cutting-edge technologies into ACE and ATAP.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Artificial Intelligence:</strong> AI/ML models for trade analytics</Typography></li>
                <li><Typography variant="body2"><strong>Machine Learning:</strong> Predictive algorithms for risk assessment</Typography></li>
                <li><Typography variant="body2"><strong>Natural Language Processing:</strong> Document analysis and classification</Typography></li>
                <li><Typography variant="body2"><strong>Computer Vision:</strong> Image recognition for cargo inspection</Typography></li>
                <li><Typography variant="body2"><strong>Blockchain:</strong> Supply chain transparency and verification</Typography></li>
                <li><Typography variant="body2"><strong>IoT Integration:</strong> Real-time cargo tracking and monitoring</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Advanced Analytics Expansion
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Enhanced analytical capabilities for trade intelligence.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Predictive Analytics:</strong> Forecasting trade patterns and risks</Typography></li>
                <li><Typography variant="body2"><strong>Network Analysis:</strong> Supply chain relationship mapping</Typography></li>
                <li><Typography variant="body2"><strong>Anomaly Detection:</strong> Identifying unusual trade activity</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Analytics:</strong> Live data processing and alerts</Typography></li>
                <li><Typography variant="body2"><strong>Data Mining:</strong> Pattern discovery in large datasets</Typography></li>
                <li><Typography variant="body2"><strong>Visualization Tools:</strong> Interactive analytics dashboards</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Cloud Modernization
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Migration and optimization for cloud infrastructure.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Cloud Migration:</strong> AWS/Azure platform migration</Typography></li>
                <li><Typography variant="body2"><strong>Microservices:</strong> Service-oriented architecture</Typography></li>
                <li><Typography variant="body2"><strong>Containerization:</strong> Docker, Kubernetes deployment</Typography></li>
                <li><Typography variant="body2"><strong>Scalability:</strong> Auto-scaling and load balancing</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Security:</strong> Enhanced security controls and compliance</Typography></li>
                <li><Typography variant="body2"><strong>Cost Optimization:</strong> Resource efficiency and cost management</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  DevSecOps & Agile Delivery
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Modern development and security practices.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Agile Development:</strong> SAFe, Scrum, sprint-based delivery</Typography></li>
                <li><Typography variant="body2"><strong>CI/CD Pipelines:</strong> Automated build, test, deploy</Typography></li>
                <li><Typography variant="body2"><strong>Security Integration:</strong> DevSecOps, security scanning</Typography></li>
                <li><Typography variant="body2"><strong>Automated Testing:</strong> Unit, integration, performance tests</Typography></li>
                <li><Typography variant="body2"><strong>Infrastructure as Code:</strong> Terraform, CloudFormation</Typography></li>
                <li><Typography variant="body2"><strong>Continuous Monitoring:</strong> Application performance management</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Joint Venture Benefits */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Joint Venture Structure & Benefits
      </Typography>

      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            SBA-Approved Mentor-Protégé Joint Venture
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Dev Technology Group (Mentor):
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Prime contractor with extensive CBP experience</Typography></li>
                <li><Typography variant="body2">SBA-approved mentor status</Typography></li>
                <li><Typography variant="body2">Deep technical expertise in ACE and ATAP</Typography></li>
                <li><Typography variant="body2">Proven track record with $184M cargo systems contract</Typography></li>
                <li><Typography variant="body2">Agile development and DevSecOps capabilities</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                C. Mack Solutions (Protégé):
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Small business partner bringing innovation</Typography></li>
                <li><Typography variant="body2">Specialized capabilities in emerging technologies</Typography></li>
                <li><Typography variant="body2">Agile and responsive development approach</Typography></li>
                <li><Typography variant="body2">Diverse workforce and fresh perspectives</Typography></li>
                <li><Typography variant="body2">Commitment to small business growth</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Contract Value & Impact */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Value & Mission Impact
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #003366' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                $136M
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contract Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                3-year agreement
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                2
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Major Systems
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ACE and ATAP platforms
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                2025-2028
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contract Period
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                3-year modernization
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                $2.5T+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Trade Supported
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Annual U.S. trade value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Mission Impact & Strategic Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Modernization:</strong> Next-generation ACE capabilities</Typography></li>
                <li><Typography variant="body2"><strong>Advanced Analytics:</strong> AI/ML-powered trade intelligence</Typography></li>
                <li><Typography variant="body2"><strong>Emerging Technologies:</strong> Blockchain, IoT, computer vision integration</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Migration:</strong> Scalable, resilient infrastructure</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Enhanced Security:</strong> Improved threat detection and risk assessment</Typography></li>
                <li><Typography variant="body2"><strong>Trade Facilitation:</strong> Faster processing, better user experience</Typography></li>
                <li><Typography variant="body2"><strong>Small Business Growth:</strong> Mentor-protégé partnership success</Typography></li>
                <li><Typography variant="body2"><strong>Innovation:</strong> Cutting-edge technology for border security</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reference Links */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract References & Documentation
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Link href="https://orangeslices.ai/c-mack-solutions-and-dev-technology-group-jv-wins-136m-dhs-cbp-ace-advanced-trade-analytics-program-support-task/" target="_blank" rel="noopener">
                <Typography variant="body2">OrangeSlices.ai - $136M ACE & ATAP Enhancement Award</Typography>
              </Link>
            </li>
            <li>
              <Link href="https://www.usaspending.gov/recipient/d768b60a-beaa-2e46-7aa5-6df63e232bb2-C/latest" target="_blank" rel="noopener">
                <Typography variant="body2">USASpending.gov - Dev Technology Group Profile</Typography>
              </Link>
            </li>
            <li>
              <Link href="https://devtechnology.com/blog/" target="_blank" rel="noopener">
                <Typography variant="body2">Dev Technology Group - Company Blog</Typography>
              </Link>
            </li>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ACEATAPEnhancementContractDetail;
